/* eslint-disable no-param-reassign */
import { LISTING_STATUS } from '../constants/listingStatus';
import { findListingEntity } from './entityReadHelpers';
import { upsertListing, upsertListingVariant } from './entityAdapter';
import { setActiveTabByProductChannel } from './uiAdapter';
import {
  getChannelEntryId,
  matchesChannelId,
} from './channelIdHelpers';

export { getChannelEntryId, getListingChannelId, matchesChannelId } from './channelIdHelpers';

export const getProductChannel = (product, channelId, productsState = null) => {
  if (productsState?.entities?.listings && product?._id) {
    const listing = findListingEntity(productsState, { masterProductId: product._id, channelId });
    if (!listing) return null;

    const { masterProductId, ...listingProduct } = listing;
    return {
      salesChannelId: String(listing.salesChannelId || channelId),
      listingStatus: listing.listingStatus,
      listingProduct,
    };
  }

  return product?.channels?.find((channel) => matchesChannelId(channel, channelId)) ?? null;
};

export const markProductChannelsAsDraft = (product, channelIds = [], state = null) => {
  if (!state || !product?._id) return;

  channelIds.forEach((channelId) => {
    const listing = findListingEntity(state, { masterProductId: product._id, channelId });
    if (!listing) return;

    setActiveTabByProductChannel(state, product._id, channelId, LISTING_STATUS.DRAFT);

    upsertListing(state, {
      ...listing,
      live: null,
      listingStatus: LISTING_STATUS.DRAFT,
    });
  });
};

export const getLiveChannelNames = (productsState, variant) => {
  if (!variant?._id) return [];

  if (productsState?.entities?.listingVariants) {
    return Object.values(productsState.entities.listingVariants)
      .filter((listingVariant) => listingVariant.masterVariantId === variant._id && listingVariant?.live?.storeId)
      .map((channel) => getChannelEntryId(channel));
  }

  return (variant?.channels || [])
    .filter((variantChannel) => variantChannel?.live?.storeId)
    .map((channel) => getChannelEntryId(channel));
};

export const isLastLiveVariantsOnChannel = (product, variantIds, channelId, productsState = null) => {
  const otherVariantsLiveOnChannel = (product?.variants || [])
    .filter((productVariant) => !variantIds.includes(productVariant._id))
    .some((productVariant) => {
      if (productsState?.entities?.listingVariants) {
        return Object.values(productsState.entities.listingVariants).some(
          (listingVariant) => (
            listingVariant.masterVariantId === productVariant._id
            && matchesChannelId(listingVariant, channelId)
            && listingVariant?.live?.storeId
          ),
        );
      }

      return (productVariant?.channels || []).some(
        (variantChannel) => matchesChannelId(variantChannel, channelId) && variantChannel?.live?.storeId,
      );
    });

  return !otherVariantsLiveOnChannel;
};

export const getLastLiveChannelsForVariantGroup = (product, variants, listingVariantIds, productsState = null) => {
  if (!listingVariantIds.length) return [];

  const variantIds = variants.map(({ _id }) => _id);

  const liveChannelIds = listingVariantIds.flatMap((listingVariantId) => {
    const listingVariant = productsState?.entities?.listingVariants?.[listingVariantId]
      || variants.flatMap((variant) => (variant.channels || [])).find(({ _id }) => _id === listingVariantId);

    if (!listingVariant?.live?.storeId) return [];

    return [getChannelEntryId(listingVariant)];
  });

  return [...new Set(liveChannelIds)].filter(
    (channelId) => isLastLiveVariantsOnChannel(product, variantIds, channelId, productsState),
  );
};

export const updateListingVariantInState = (state, { masterProductId, channelId, data }) => {
  if (!data?._id) return;

  const existingVariant = Object.values(state.entities?.listingVariants || {}).find(
    (listingVariant) => listingVariant._id === data._id,
  );

  upsertListingVariant(state, {
    ...(existingVariant || {}),
    ...data,
    masterVariantId: existingVariant?.masterVariantId || data.masterVariantId,
  });
};
